import { MaterialGroup } from '@cutr/constants/cutlist';
import { Packet } from '@cutr/nesting';
import cn from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { getMaterial } from '@/api/materials';
import { useActiveGroup, useMaterialGroupState } from '@/api/materialsGroup';
import {
  SheetGroup,
  useNestingStatusStore,
  useNestingStore,
} from '@/api/nesting';
import { BasePagination } from '@/blocks/BasePagination';
import { ProgressIndicator } from '@/primitives/ProgressIndicator';

import styles from './AgentNesting.module.css';
import { RenderSheet } from './NestingRenderer';
import nestingRendererStyles from './NestingRenderer.module.css';

export const useActiveSheetGroup = () => {
  const activeTab = useMaterialGroupState((state) => state.activeGroup);
  const sheetGroup = useNestingStore((state) =>
    state.sheetGroups.find((group) =>
      group.parts.find((p) => p.groupId === activeTab)
    )
  );
  return sheetGroup as SheetGroup;
};

export const AgentNesting = () => {
  const group = useActiveSheetGroup();
  const { t } = useTranslation();
  const activeGroup = useActiveGroup();
  const sheetStackingType = activeGroup?.sheetStackingType;

  if (!group) return null;

  const thickness = group.sheetConfig.thicknessUM / 1000 + 'mm';
  const size: [number, number] = [
    group.sheetConfig.lengthMM,
    group.sheetConfig.widthMM,
  ];
  const sheetDimensions =
    group.materialGroup.sheetSizeSelection === 'automatic'
      ? (group.materialGroup.automaticSheetSizeMaterials
          .map((code) => {
            const material = getMaterial(code);
            if (!material) return null;
            const isSelected =
              material.lengthMM === group.sheetConfig.lengthMM &&
              material.widthMM === group.sheetConfig.widthMM;
            const dimensionString = `${material.lengthMM} x ${material.widthMM}`;
            return {
              dimensionString: isSelected
                ? `${dimensionString} (${t(
                    `cutlist-summary.nesting.sheetCount`,
                    {
                      count: group.sheets.length,
                    }
                  )})`
                : dimensionString,
              isSelected,
            };
          })
          .filter(Boolean) as {
          dimensionString: string;
          isSelected: boolean;
        }[])
      : [
          {
            dimensionString: `${group.sheetConfig.lengthMM} x ${group.sheetConfig.widthMM}`,
            isSelected: true,
          },
        ];

  return (
    <div className={styles.layout} id="nesting-parts">
      <div id={group.materialGroup.id}>
        <div className={styles.infoContainer}>
          <div className={styles.info}>
            <label>{t(`cutlist-summary.nesting.partInfo.thickness`)}</label>
            <strong>{thickness}</strong>
          </div>
          <div className={styles.info}>
            <label>{'Margin'}</label>
            {group.sheetConfig.sheetEdgeTrimConfig && (
              <strong>
                {`↓: ${group.sheetConfig.sheetEdgeTrimConfig.trimThickness.length1TrimThicknessMM}
                      ↑: ${group.sheetConfig.sheetEdgeTrimConfig.trimThickness.length2TrimThicknessMM}
                      ←: ${group.sheetConfig.sheetEdgeTrimConfig.trimThickness.width1TrimThicknessMM}
                      →: ${group.sheetConfig.sheetEdgeTrimConfig.trimThickness.width2TrimThicknessMM}`}
              </strong>
            )}
          </div>
          <div className={styles.info}>
            <label>{t(`cutlist-summary.nesting.partInfo.size`)}</label>
            {sheetDimensions.map(({ dimensionString, isSelected }) => (
              <strong
                key={dimensionString}
                className={cn(
                  styles.info,
                  isSelected
                    ? styles.selectedDimension
                    : styles.notSelectedDimension
                )}
              >
                {dimensionString}
              </strong>
            ))}
          </div>
        </div>
      </div>

      <NestingVisualizer
        size={size}
        group={group}
        sheetStackingType={sheetStackingType}
      />
    </div>
  );
};

const NestingVisualizer = ({
  size,
  group,
  sheetStackingType,
}: {
  size: [number, number];
  group: SheetGroup;
  sheetStackingType: MaterialGroup['sheetStackingType'];
}) => {
  const nestingStatus = useNestingStatusStore((state) => ({
    status: state.status[group.materialGroup.id],
    progress: state.progress[group.materialGroup.id],
  }));
  const { t } = useTranslation();
  const [w, h] = size;
  const ratio = h / w;
  const wrapperWidth = ratio > 0.6 ? '50vw' : 'auto';

  if (nestingStatus.status === 'running') {
    return (
      <section
        className={cn(nestingRendererStyles.nestingSheetWrapper)}
        style={{ width: wrapperWidth }}
      >
        <article
          className={nestingRendererStyles.nestingSheet}
          style={
            { '--paddingB': `calc(${ratio} * 100%)` } as React.CSSProperties
          }
        >
          <aside className={styles.progressIndicatorOverlay}>
            <ProgressIndicator
              value={nestingStatus.progress?.value ?? 0}
              label={t(`agent.nestingInProgress`)}
            />
          </aside>
        </article>
      </section>
    );
  }

  if (!sheetStackingType || sheetStackingType === 'disabled') {
    return (
      <NestingPagination navKey="sheet">
        {group.sheets.map((sheet, idx) => (
          <div key={idx}>
            <RenderSheet
              size={size}
              sheet={sheet}
              sheetEdgeTrimConfig={group.sheetConfig.sheetEdgeTrimConfig}
              wrapperWidth={wrapperWidth}
            />
          </div>
        ))}
      </NestingPagination>
    );
  }

  if (sheetStackingType === 'enabled') {
    return (
      <NestingPagination navKey="packet" packets={group.packets}>
        {group?.packets?.map((packet, idx) => {
          const sheet = group.sheets.find((sheet) =>
            packet.sheetIds.includes(String(sheet.sheetId))
          );

          return (
            <div key={idx}>
              <RenderSheet
                size={size}
                sheet={sheet}
                sheetEdgeTrimConfig={group.sheetConfig.sheetEdgeTrimConfig}
                wrapperWidth={wrapperWidth}
              />
            </div>
          );
        })}
      </NestingPagination>
    );
  }

  return null;
};

const NestingPagination = ({
  children,
  navKey,
  packets,
}: {
  children: React.ReactElement[];
  navKey: 'sheet' | 'packet';
  packets?: Packet[];
}) => {
  const { t } = useTranslation();
  const container = React.useRef<HTMLDivElement>(null);
  const [page, setPage] = React.useState(1);
  const pages = React.Children.count(children);

  React.useEffect(() => {
    const isEmpty = pages === 0;
    const isOutOfBounds = page > pages;

    if (isEmpty || isOutOfBounds) {
      setPage(1);
      return;
    }
  }, [page, pages]);

  const childrenArray = React.Children.toArray(children);
  const currentChild = childrenArray[page - 1] as
    | React.ReactElement
    | undefined;

  return (
    <div>
      <div ref={container}>{currentChild}</div>
      <div className={styles.navigationWrapper}>
        <div className={styles.navInfo}>
          <span>{t(`translation.pagination.${navKey}`, { page, pages })}</span>
          {navKey === 'packet' && (
            <span>
              {t(`agent.quoteFlow.packet`, {
                count: packets?.[page - 1]?.sheetIds.length,
              })}
            </span>
          )}
        </div>
        <BasePagination
          totalPages={pages}
          currentPage={page}
          onPageChanged={setPage}
        />
      </div>
    </div>
  );
};
